import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'

import { dmsState } from '@/app/store'
import { type TLoaderType } from '@/pages/Dms/helper'
import { Modal } from '@/shared'
import { EToastings } from '@App/types/common'
import { onToasting } from '@App/utils'

import style from './DocLoaderModal.module.scss'
import Error from './error.svg'
import { ErrorsComponent } from './ErrorsComponent/ErrorsComponent'
import { FilePicker } from './FilePicker'
import { ImportHistory } from './ImportHistory/ImportHistory'
import { ImportProgress } from './ImportProgress/ImportProgress'
import { useSignalRNotifications } from './useSignalRNotifications'

interface IProps {
    show: boolean
    fileType: TLoaderType
    errors: string[]
    onClose: () => void
    onGetLastErrors: () => void
    onUploadFileCallback: (file: File, isAddedAttach: boolean) => void
    onGetImportInfoByIdHandler: (key: string) => void
}

export const DocLoaderModal = observer((props: IProps) => {
    const { show, fileType, errors, onClose, onGetLastErrors, onUploadFileCallback, onGetImportInfoByIdHandler } = props
    const [isSuccess, setIsSuccess] = useState<boolean>(false)

    const onSuccessProgressBar = () => {
        setIsSuccess(true)
        onToasting({
            type: EToastings.SUCCESS,
            message: 'Импорт завершен успешно'
        })
        onClose()
    }

    const { isConnected, notifications, clearNotifications } = useSignalRNotifications({
        onGetErrors: onGetImportInfoByIdHandler,
        onSuccess: onSuccessProgressBar
    })

    // const percents = notifications[notifications.length - 1]?.Info?.Percent
    //     ? `${notifications[notifications.length - 1]?.Info?.Percent}%`
    //     : '0%'

    useEffect(
        () => () => {
            clearNotifications()
        },
        []
    )

    const lastPercent = useMemo(() => {
        const lastNotification = notifications[notifications.length - 1]
        return lastNotification?.Info?.Percent ? `${lastNotification.Info.Percent}%` : null
    }, [notifications])

    const lastLabel = useMemo(() => {
        const lastNotification = notifications[notifications.length - 1]
        return lastNotification?.Info?.InsuraceContract ? `${lastNotification.Info.InsuraceContract}` : null
    }, [notifications])

    const HEADER_MAPPER: Record<TLoaderType, string> = {
        importList: 'Импортирование списка',
        detachPatients: 'Открепление пациентов'
    }

    const allowedFormats = ['xlsx']

    const filesDataCallback = (file: File, isAddedAttach: boolean) => {
        if (dmsState.isFetching) return
        const fileNameContent = file?.name?.split('.')
        const currentExtention = file?.name?.split('.')[fileNameContent.length - 1]
        if (allowedFormats.includes(currentExtention)) {
            onUploadFileCallback(file, isAddedAttach)
        } else {
            onToasting({
                type: EToastings.ERROR,
                message: `Допускаются файлы с расширением ${allowedFormats.join(' ')}`
            })
        }
    }

    // const sgnalrInfo: string | null = null

    return (
        <Modal show={show} onClose={onClose} modalStyles={style.modalStyles}>
            <div className={style.container}>
                <div className={style.header}>
                    <span>{HEADER_MAPPER[fileType]}</span>
                </div>
                <div className={style.download}>
                    <button
                        style={{
                            opacity: dmsState.isFetching ? 0.5 : 1,
                            cursor: dmsState.isFetching ? 'default' : 'pointer'
                        }}
                        onClick={() => !dmsState.isFetching && dmsState.onGetImportTemplate(fileType === 'importList')}
                    >
                        Скачать шаблон
                    </button>
                </div>
                {(!isConnected || dmsState.isFetching) && (
                    <div className={style.connection}>
                        <h4>Подключение...</h4>
                    </div>
                )}
                {(isConnected || !dmsState.isFetching) && (
                    <div className={style.filePicker}>
                        {lastPercent === null || isSuccess ? (
                            <>
                                {fileType === 'importList' && (
                                    <FilePicker
                                        title='Добавить список'
                                        disabled={dmsState.isFetching}
                                        className={classNames(
                                            style.fileLoader,
                                            dmsState.isFetching && style.fileLoaderDisabled
                                        )}
                                        onHandleFile={filesDataCallback}
                                        isAddedAttach={true}
                                    />
                                )}
                                {fileType === 'importList' && (
                                    <FilePicker
                                        title='Изменить список'
                                        disabled={dmsState.isFetching}
                                        className={classNames(
                                            style.fileLoader,
                                            dmsState.isFetching && style.fileLoaderDisabled
                                        )}
                                        onHandleFile={filesDataCallback}
                                        isAddedAttach={false}
                                    />
                                )}
                                {fileType === 'detachPatients' && (
                                    <FilePicker
                                        title='Загрузить список'
                                        disabled={dmsState.isFetching}
                                        className={classNames(
                                            style.fileLoader,
                                            dmsState.isFetching && style.fileLoaderDisabled
                                        )}
                                        onHandleFile={filesDataCallback}
                                        isAddedAttach={false}
                                    />
                                )}
                            </>
                        ) : (
                            <ImportProgress percents={lastPercent} label={lastLabel} />
                        )}
                    </div>
                )}
                {errors.length > 0 && (
                    <div className={style.buttonWrapper}>
                        <button onClick={onGetLastErrors}>
                            <div className={style.iconWrapper}>
                                <Error />
                            </div>
                            Выгрузка ошибок
                        </button>
                    </div>
                )}
                {errors.length < 1 && <ImportHistory />}
                {errors.length > 0 && <ErrorsComponent errors={errors} />}
            </div>
        </Modal>
    )
})
